import { Link, useNavigate } from 'react-router-dom';
import React, { useEffect } from 'react';
import { Mail, Lock, Eye, EyeOff, Loader2, ArrowRight, Check, X, ShieldCheck, CheckCircle2, ArrowRightCircle } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
    registerUser,
    clearError,
    clearRegistrationSuccess,
    selectIsLoading,
    selectAuthError,
    selectIsAuthenticated,
    selectRegistrationSuccess,
    selectRegisteredEmail,
} from '../store/slices/authSlice';

const Register = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const isLoading = useAppSelector(selectIsLoading);
    const error = useAppSelector(selectAuthError);
    const isAuthenticated = useAppSelector(selectIsAuthenticated);
    const registrationSuccess = useAppSelector(selectRegistrationSuccess);
    const registeredEmail = useAppSelector(selectRegisteredEmail);

    const [email, setEmail] = React.useState('');
    const [password, setPassword] = React.useState('');
    const [confirmPassword, setConfirmPassword] = React.useState('');
    const [showPassword, setShowPassword] = React.useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
    const [validationError, setValidationError] = React.useState('');

    // If already logged in, go to dashboard
    useEffect(() => {
        if (isAuthenticated) {
            navigate('/dashboard', { replace: true });
        }
    }, [isAuthenticated, navigate]);

    // Clear registration success on unmount (but NOT the error — let it persist)
    useEffect(() => {
        return () => {
            dispatch(clearRegistrationSuccess());
        };
    }, [dispatch]);

    // Clear error only when component first mounts (fresh navigation to /register)
    useEffect(() => {
        dispatch(clearError());
    }, [dispatch]);

    // Password strength checks
    const passwordChecks = {
        length: password.length >= 10,
        uppercase: /[A-Z]/.test(password),
        lowercase: /[a-z]/.test(password),
        number: /[0-9]/.test(password),
        special: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(password),
    };

    const passedCount = Object.values(passwordChecks).filter(Boolean).length;
    const totalChecks = Object.keys(passwordChecks).length;
    const allChecksPassed = passedCount === totalChecks;

    // Strength level: weak / fair / good / strong
    const getStrengthLevel = () => {
        if (password.length === 0) return { label: '', color: '', barColor: '', width: '0%' };
        if (passedCount <= 2) return { label: 'Weak', color: 'text-red-500', barColor: 'bg-red-500', width: '25%' };
        if (passedCount <= 3) return { label: 'Fair', color: 'text-amber-500', barColor: 'bg-amber-500', width: '50%' };
        if (passedCount <= 4) return { label: 'Good', color: 'text-emerald-500', barColor: 'bg-emerald-500', width: '75%' };
        return { label: 'Strong', color: 'text-emerald-600', barColor: 'bg-emerald-600', width: '100%' };
    };

    const strength = getStrengthLevel();

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isLoading) return; // Prevent double-submit
        setValidationError('');

        if (!allChecksPassed) {
            setValidationError('Password must meet all strength requirements below.');
            return;
        }

        if (password !== confirmPassword) {
            setValidationError('Passwords do not match.');
            return;
        }

        if (password.length > 128) {
            setValidationError('Password must be 128 characters or fewer.');
            return;
        }

        dispatch(registerUser({ email, password }));
    };

    const handleInputChange = (setter) => (e) => {
        setter(e.target.value);
        setValidationError('');
        if (error) dispatch(clearError());
    };

    const handleGoToLogin = () => {
        dispatch(clearRegistrationSuccess());
        navigate('/login', { replace: true });
    };

    const displayError = validationError || error;

    return (
        <div className="min-h-screen flex relative">
            {/* ============================================ */}
            {/* Registration Success Dialog                  */}
            {/* ============================================ */}
            {registrationSuccess && (
                <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
                    {/* Backdrop */}
                    <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" />

                    {/* Dialog */}
                    <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 animate-in">
                        {/* Success icon */}
                        <div className="flex justify-center mb-6">
                            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center">
                                <CheckCircle2 size={36} className="text-emerald-600" />
                            </div>
                        </div>

                        <h3 className="text-xl font-display font-bold text-gray-900 text-center mb-2">
                            Account created successfully!
                        </h3>

                        <p className="text-gray-500 text-center text-sm mb-5 leading-relaxed">
                            We've sent an onboarding email to{' '}
                            <span className="font-semibold text-gray-700">{registeredEmail}</span>.
                            Please sign in to set up your organisation and start serving your customers.
                        </p>

                        {/* Next steps */}
                        <div className="bg-gray-50 rounded-xl p-4 mb-6">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">What's next</p>
                            <div className="space-y-2.5">
                                {[
                                    'Sign in with your new credentials',
                                    'Provision your organisation and staff',
                                    'Start serving your customers',
                                ].map((step, i) => (
                                    <div key={i} className="flex items-center gap-2.5">
                                        <div className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
                                            <span className="text-[10px] font-bold">{i + 1}</span>
                                        </div>
                                        <span className="text-sm text-gray-600">{step}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <button
                            onClick={handleGoToLogin}
                            className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm bg-emerald-600 hover:bg-emerald-700 active:scale-[0.98] text-white shadow-sm transition-all"
                        >
                            Go to Sign In
                            <ArrowRightCircle size={16} />
                        </button>
                    </div>
                </div>
            )}
            {/* Left panel - Branding */}
            <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-emerald-600 via-green-700 to-teal-800 relative overflow-hidden">
                <div className="absolute inset-0">
                    <div className="absolute top-20 left-20 w-72 h-72 bg-white/5 rounded-full blur-3xl" />
                    <div className="absolute bottom-32 right-16 w-96 h-96 bg-emerald-400/10 rounded-full blur-3xl" />
                    <div className="absolute top-1/2 left-1/3 w-48 h-48 bg-teal-300/10 rounded-full blur-2xl" />
                    <div className="absolute inset-0 opacity-[0.03]"
                         style={{
                             backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
                             backgroundSize: '60px 60px'
                         }}
                    />
                </div>

                <div className="relative z-10 flex flex-col justify-between p-12 w-full">
                    <div>
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                                <span className="text-white font-display font-bold text-lg">W</span>
                            </div>
                            <span className="text-white font-display font-bold text-2xl tracking-tight">wadakaroo</span>
                        </div>
                    </div>

                    <div className="max-w-md">
                        <h1 className="text-4xl font-display font-bold text-white leading-tight mb-10">
                            Create your<br />
                            <span className="text-emerald-200">account</span>
                        </h1>

                        {/* Steps preview */}
                        <div className="space-y-4">
                            {[
                                { step: 1, label: 'Receive the onboarding email' },
                                { step: 2, label: 'Provision your organisation and staff' },
                                { step: 3, label: 'Start serving your customers' },
                            ].map(({ step, label }) => (
                                <div key={step} className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold bg-white/10 text-white/70">
                                        {step}
                                    </div>
                                    <span className="text-white/70">
                                        {label}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="flex items-center gap-8 text-emerald-200/60 text-sm">
                        <span>© 2025 Wadakaroo</span>
                        <span>·</span>
                        <span>Privacy</span>
                        <span>·</span>
                        <span>Terms</span>
                    </div>
                </div>
            </div>

            {/* Right panel - Registration form */}
            <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12 bg-gray-50">
                <div className="w-full max-w-md">
                    {/* Mobile logo */}
                    <div className="lg:hidden flex items-center gap-3 mb-10">
                        <div className="w-10 h-10 bg-emerald-600 rounded-xl flex items-center justify-center">
                            <span className="text-white font-display font-bold text-lg">W</span>
                        </div>
                        <span className="text-gray-900 font-display font-bold text-2xl tracking-tight">wadakaroo</span>
                    </div>

                    {/* Step indicator (mobile) */}
                    <div className="lg:hidden flex items-center gap-2 mb-6">
                        <div className="flex items-center gap-1.5">
                            <div className="w-8 h-1.5 bg-emerald-500 rounded-full" />
                            <div className="w-8 h-1.5 bg-gray-200 rounded-full" />
                            <div className="w-8 h-1.5 bg-gray-200 rounded-full" />
                        </div>
                        <span className="text-xs text-gray-400 ml-2">Step 1 of 3</span>
                    </div>

                    <div className="mb-8">
                        <h2 className="text-2xl font-display font-bold text-gray-900 mb-2">
                            Create your account
                        </h2>
                        <p className="text-gray-500">
                            Start with your email and a secure password
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-5">
                        {displayError && (
                            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                                <p className="text-red-700 text-sm">{displayError}</p>
                            </div>
                        )}

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                Email address
                            </label>
                            <div className="relative">
                                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                <input
                                    type="email"
                                    value={email}
                                    onChange={handleInputChange(setEmail)}
                                    className="w-full pl-11 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none text-gray-900 placeholder-gray-400"
                                    placeholder="you@company.com"
                                    required
                                    autoComplete="email"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                Password
                            </label>
                            <div className="relative">
                                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={handleInputChange(setPassword)}
                                    className="w-full pl-11 pr-12 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none text-gray-900 placeholder-gray-400"
                                    placeholder="Create a strong password"
                                    required
                                    autoComplete="new-password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 bg-transparent border-none cursor-pointer"
                                >
                                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>

                            {/* Password strength meter */}
                            {password.length > 0 && (
                                <div className="mt-3 space-y-3">
                                    {/* Strength bar */}
                                    <div className="flex items-center gap-3">
                                        <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                            <div
                                                className={`h-full rounded-full transition-all duration-500 ease-out ${strength.barColor}`}
                                                style={{ width: strength.width }}
                                            />
                                        </div>
                                        <div className="flex items-center gap-1">
                                            {allChecksPassed && <ShieldCheck size={13} className="text-emerald-600" />}
                                            <span className={`text-xs font-semibold ${strength.color}`}>
                                                {strength.label}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Requirement checklist */}
                                    <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                                        {[
                                            { key: 'length', label: '10+ characters' },
                                            { key: 'uppercase', label: 'Uppercase letter' },
                                            { key: 'lowercase', label: 'Lowercase letter' },
                                            { key: 'number', label: 'Number' },
                                            { key: 'special', label: 'Special character' },
                                        ].map(({ key, label }) => (
                                            <div key={key} className="flex items-center gap-1.5">
                                                <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
                                                    passwordChecks[key]
                                                        ? 'bg-emerald-100 text-emerald-600'
                                                        : 'bg-red-50 text-red-400'
                                                }`}>
                                                    {passwordChecks[key]
                                                        ? <Check size={10} strokeWidth={3} />
                                                        : <X size={9} strokeWidth={3} />
                                                    }
                                                </div>
                                                <span className={`text-xs ${
                                                    passwordChecks[key] ? 'text-emerald-600' : 'text-gray-400'
                                                }`}>
                                                    {label}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                Confirm password
                            </label>
                            <div className="relative">
                                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                <input
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    value={confirmPassword}
                                    onChange={handleInputChange(setConfirmPassword)}
                                    className={`w-full pl-11 pr-12 py-3 bg-white border rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none text-gray-900 placeholder-gray-400 ${
                                        confirmPassword
                                            ? confirmPassword === password
                                                ? 'border-emerald-300'
                                                : 'border-red-300'
                                            : 'border-gray-200'
                                    }`}
                                    placeholder="Repeat your password"
                                    required
                                    autoComplete="new-password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 bg-transparent border-none cursor-pointer"
                                >
                                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                            {confirmPassword && confirmPassword !== password && (
                                <div className="flex items-center gap-1.5 mt-1.5">
                                    <X size={12} className="text-red-500" />
                                    <p className="text-red-500 text-xs">Passwords do not match</p>
                                </div>
                            )}
                            {confirmPassword && confirmPassword === password && (
                                <div className="flex items-center gap-1.5 mt-1.5">
                                    <Check size={12} className="text-emerald-600" strokeWidth={3} />
                                    <p className="text-emerald-600 text-xs">Passwords match</p>
                                </div>
                            )}
                        </div>

                        <button
                            type="submit"
                            disabled={isLoading || !allChecksPassed || !confirmPassword || confirmPassword !== password}
                            className={`w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all ${
                                isLoading || !allChecksPassed || !confirmPassword || confirmPassword !== password
                                    ? 'bg-emerald-300 cursor-not-allowed'
                                    : 'bg-emerald-600 hover:bg-emerald-700 active:scale-[0.98]'
                            } text-white shadow-sm`}
                        >
                            {isLoading ? (
                                <>
                                    <Loader2 className="animate-spin" size={18} />
                                    Creating account...
                                </>
                            ) : (
                                <>
                                    Create account
                                    <ArrowRight size={16} />
                                </>
                            )}
                        </button>
                    </form>

                    <div className="mt-8 text-center">
                        <p className="text-sm text-gray-500">
                            Already have an account?{' '}
                            <Link
                                to="/login"
                                className="text-emerald-600 hover:text-emerald-700 font-semibold"
                            >
                                Sign in
                            </Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Register;